import { yupResolver } from '@hookform/resolvers/yup';
import {
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFeedbackButtonsContext } from 'pages/ParentHelper/components/Chat/components/Body/components/Answer/components/FeedbackButtons/hooks';
import { useParentHelperContext } from 'pages/ParentHelper/providers/ParentHelperProvider/hooks';
import { useSnackbarContext } from 'pages/ParentHelper/providers/SnackbarProvider/hooks';
import { SnackbarEnum } from 'pages/ParentHelper/providers/SnackbarProvider/types';
import { FormOutlinedInput } from 'shared/components/form/components/FormOutlinedInputField';
import { FormRadioGroup } from 'shared/components/form/components/FormRadioGroup';

import { DialogActions } from './components/DialogActions';
import { FieldsNames, MAX_LETTER_COUNT, FeedbackTextValues } from './constants';
import { useValidation, useDefaultValues } from './hooks';
import { transformPostData } from './utils';

import type { FeedbackType } from 'pages/ParentHelper/components/Chat/components/Body/components/Answer/components/FeedbackButtons/components/FeedbackDislikeDialog/types';
import type { FC } from 'react';

export const FeedbackDislikeDialogForm: FC = () => {
  const { t } = useTranslation();
  const validationSchema = useValidation();
  const { dislikeMessage } = useParentHelperContext();
  const { onClose } = useFeedbackButtonsContext();
  const { onOpen: onOpenSnackbar } = useSnackbarContext();
  const defaultValues = useDefaultValues();

  const methods = useForm<FeedbackType>({
    defaultValues,
    mode: 'onSubmit',
    resolver: yupResolver<FeedbackType>(validationSchema),
  });

  const handleSubmit = methods.handleSubmit(async (data: FeedbackType) => {
    const transformedData = transformPostData(data);

    try {
      await dislikeMessage(transformedData);

      onOpenSnackbar(SnackbarEnum.Feedback);
    } finally {
      onClose();
    }
  });

  const feedbackText = methods.watch(
    'inspiredGlobalStudyPlatformApiModelsParentHelperAnswerFeedbackRequest.feedbackText',
  );

  const shouldShowInputField = useMemo(
    () => feedbackText === FeedbackTextValues.other,
    [feedbackText],
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {t('pages.parentHelper.chat.body.feedbackDislikeDialog.title')}
        </DialogTitle>
        <DialogContent>
          <FormControl sx={{ ml: 1 }}>
            <FormRadioGroup name={FieldsNames.FeedbackText}>
              {Object.values(FeedbackTextValues).map(value => (
                <FormControlLabel
                  key={value}
                  value={value}
                  control={<Radio />}
                  label={value}
                />
              ))}
            </FormRadioGroup>
          </FormControl>
          {shouldShowInputField && (
            <FormOutlinedInput
              name={FieldsNames.OtherFeedbackText}
              placeholder={t(
                'pages.parentHelper.chat.body.feedbackDislikeDialog.inputPlaceholder',
              )}
              rows="3"
              maxLetterCount={MAX_LETTER_COUNT}
              sx={{ mt: 2.5 }}
            />
          )}
        </DialogContent>
        <DialogActions />
      </form>
    </FormProvider>
  );
};
